import React, { useState } from "react";
import { Package, User, Calendar, ChevronRight, Phone, Mail, CheckCircle, Clock } from "lucide-react";
import { ComponentProps, Case } from "../types/types";
import { supabaseService } from "../SupabaseService";

const KlarTilHenting: React.FC<ComponentProps> = ({
  cases,
  setCases,
  customers,
  openCaseForEditing,
}) => {
  const [isMoving, setIsMoving] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeliveryDialog, setShowDeliveryDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
  const [selectedPerson, setSelectedPerson] = useState("");

  const utlevertPersons = [
    "Johan",
    "Odd Arthur",
    "Rose",
    "Tormod",
    "Trond Øyvind",
  ];

  const klarTilHentingCases = cases.filter(
    (case_) => case_.kategori === "Klar til henting"
  );

  // Filtrer basert på søk
  const filteredCases = klarTilHentingCases.filter(
    (case_) =>
      case_.kunde.toLowerCase().includes(searchTerm.toLowerCase()) ||
      case_.maskinMerke?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      case_.beskrivelse.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Grupper ordre etter kunde
  const groupedCases = filteredCases.reduce((groups, case_) => {
    const customerName = case_.kunde;
    if (!groups[customerName]) {
      groups[customerName] = [];
    }
    groups[customerName].push(case_);
    return groups;
  }, {} as Record<string, typeof filteredCases>);

  const handleMoveToFakturering = async (caseId: number) => {
    setIsMoving(caseId);
    const originalCases = [...cases];
    
    try {
      const caseToUpdate = cases.find(c => c.id === caseId);
      if (!caseToUpdate) throw new Error("Sak ikke funnet");
      
      const updatedCase = {
        ...caseToUpdate,
        kategori: "Klar til fakturering",
        opprettet: new Date().toISOString(),
      };
      
      setCases(prev =>
        prev.map(case_ =>
          case_.id === caseId ? updatedCase : case_
        )
      );
      
      const saved = await supabaseService.saveCase(updatedCase);
      if (!saved) {
        throw new Error("Kunne ikke lagre endring");
      }
      
      console.log(`✅ Flyttet sak ${caseId} til Klar til fakturering`);
    } catch (error) {
      setCases(originalCases);
      console.error("Feil ved flytting av sak:", error);
    } finally {
      setIsMoving(null);
    }
  };

  const handleMoveToArchive = async (caseId: number, utlevertAv: string) => {
    setIsMoving(caseId);
    const originalCases = [...cases];
    
    try {
      const caseToUpdate = cases.find(c => c.id === caseId);
      if (!caseToUpdate) throw new Error("Sak ikke funnet");
      
      const updatedCase = {
        ...caseToUpdate,
        kategori: "Arkiv",
        utlevertAv,
        opprettet: new Date().toISOString(),
      };
      
      setCases(prev =>
        prev.map(case_ =>
          case_.id === caseId ? updatedCase : case_
        )
      );
      
      const saved = await supabaseService.saveCase(updatedCase);
      if (!saved) {
        throw new Error("Kunne ikke lagre endring");
      }
      
      console.log(`✅ Flyttet sak ${caseId} til Arkiv`);
    } catch (error) {
      setCases(originalCases);
      console.error("Feil ved flytting av sak:", error);
    } finally {
      setIsMoving(null);
      setShowDeliveryDialog(false);
      setShowArchiveDialog(false);
      setSelectedCaseId(null);
      setSelectedPerson("");
    }
  };

  const getDaysWaiting = (opprettet: string): number => {
    const startDate = new Date(opprettet);
    const now = new Date();
    return Math.floor((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  };

  const getWaitingBadge = (opprettet: string) => {
    const days = getDaysWaiting(opprettet);
    
    if (days > 7) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <Clock size={12} />
          {days} dager
        </span>
      );
    }
    if (days > 3) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <Clock size={12} />
          {days} dager
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle size={12} />
        Klar
      </span>
    );
  };

  const openDeliveryDialog = (caseId: number) => {
    setSelectedCaseId(caseId);
    setShowDeliveryDialog(true);
  };

  const openArchiveDialog = (caseId: number) => {
    setSelectedCaseId(caseId);
    setShowArchiveDialog(true);
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b bg-gradient-to-r from-orange-50 to-amber-50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Package className="text-orange-600" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Klar til henting
                </h2>
                <p className="text-sm text-gray-600">Ferdig reparert - venter på kunde</p>
              </div>
            </div>
            <span className="px-3 py-1.5 text-sm font-medium rounded-full bg-orange-100 text-orange-800">
              {klarTilHentingCases.length} {klarTilHentingCases.length === 1 ? 'sak' : 'saker'}
            </span>
          </div>
          
          <div className="relative">
            <input
              type="text"
              placeholder="Søk kunde eller maskin..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 pl-9 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              🔍
            </span>
          </div>
        </div>

        <div className="p-4">
          {filteredCases.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <Package className="text-gray-400" size={32} />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm ? "Ingen treff" : "Ingen saker klar til henting"}
              </h3>
              <p className="text-gray-600">
                {searchTerm ? "Prøv et annet søkeord" : "Ferdige saker vil vises her"}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {Object.entries(groupedCases).map(([customerName, customerCases]) => {
                // Hvis kunden kun har én ordre, vis som vanlig enkeltordre
                if (customerCases.length === 1) {
                  const case_ = customerCases[0];
                  
                  return (
                    <div
                      key={case_.id}
                      className="p-3 rounded-lg cursor-pointer transition-all border-2 border-orange-200 bg-orange-50 hover:shadow-lg hover:border-orange-300"
                      onClick={() => openCaseForEditing(case_)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <User className="text-gray-500" size={18} />
                          <h3 className="text-base font-semibold text-gray-900">
                            {case_.kunde}
                          </h3>
                        </div>
                        {getWaitingBadge(case_.opprettet)}
                      </div>

                      <div className="space-y-1 mb-2">
                        <div className="flex items-center space-x-2 text-sm text-gray-700">
                          <Package size={14} className="text-gray-400" />
                          <span className="font-medium">{case_.maskinMerke || "Ikke spesifisert"}</span>
                          {case_.serienr && (
                            <span className="text-gray-500 text-xs">• SN: {case_.serienr}</span>
                          )}
                        </div>
                        
                        <div className="text-sm text-gray-700 bg-white bg-opacity-60 rounded px-2 py-1">
                          {case_.beskrivelse}
                        </div>

                        {case_.utfortDato && (
                          <div className="flex items-center space-x-1 text-xs text-green-700">
                            <CheckCircle size={12} />
                            <span>Ferdig: {new Date(case_.utfortDato).toLocaleDateString("nb-NO")}</span>
                            {case_.serviceRepUtfort && (
                              <span>• {case_.serviceRepUtfort}</span>
                            )}
                          </div>
                        )}

                        {/* Kontaktinfo fremhevet */}
                        <div className="bg-white rounded p-2 border border-orange-200">
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            {case_.tlf && (
                              <div className="flex items-center space-x-1">
                                <Phone size={12} className="text-orange-600" />
                                <span className="font-medium">{case_.tlf}</span>
                              </div>
                            )}
                            {case_.mail && (
                              <div className="flex items-center space-x-1">
                                <Mail size={12} className="text-orange-600" />
                                <span className="truncate">{case_.mail}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-orange-200">
                        <div className="text-xs text-gray-500">
                          ID: #{case_.id}
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveToFakturering(case_.id);
                            }}
                            disabled={isMoving === case_.id}
                            className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-xs font-medium rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            → Fakturering
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openArchiveDialog(case_.id);
                            }}
                            className="px-3 py-1.5 bg-gradient-to-r from-gray-500 to-gray-600 text-white text-xs font-medium rounded-lg hover:from-gray-600 hover:to-gray-700 transition-all transform hover:scale-105"
                          >
                            → Arkiv
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                }
                
                // Hvis kunden har flere ordre, vis som gruppert kort
                return (
                  <div
                    key={customerName}
                    className="border-2 border-gray-200 rounded-lg overflow-hidden hover:shadow-lg hover:border-orange-300 transition-all"
                  >
                    <div className="px-4 py-2 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <User className="text-gray-600" size={18} />
                        <h3 className="text-base font-semibold text-gray-900">{customerName}</h3>
                      </div>
                      <span className="px-2 py-0.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-semibold rounded-full">
                        {customerCases.length} ordre
                      </span>
                    </div>
                    
                    <div className="p-1">
                      {customerCases.map((case_) => (
                        <div
                          key={case_.id}
                          className="m-1 p-2 rounded-lg cursor-pointer transition-all bg-orange-50 border border-orange-200 hover:bg-white hover:border-orange-400 relative pl-8"
                          onClick={() => openCaseForEditing(case_)}
                        >
                          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                          
                          <div className="flex items-start justify-between mb-1">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {case_.maskinMerke || "Ikke spesifisert"}
                                {case_.serienr && (
                                  <span className="text-gray-500 text-xs ml-1">SN: {case_.serienr}</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-600">{case_.beskrivelse}</div>
                              {case_.utfortDato && (
                                <div className="text-xs text-green-700 mt-1">
                                  ✅ Ferdig: {new Date(case_.utfortDato).toLocaleDateString("nb-NO")}
                                  {case_.serviceRepUtfort && (
                                    <span> • {case_.serviceRepUtfort}</span>
                                  )}
                                </div>
                              )}
                            </div>
                            {getWaitingBadge(case_.opprettet)}
                          </div>
                          
                          <div className="flex items-center justify-between text-xs text-gray-500 mt-1.5 pt-1.5 border-t border-orange-200">
                            <span>ID: #{case_.id}</span>
                            <div className="flex space-x-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveToFakturering(case_.id);
                                }}
                                disabled={isMoving === case_.id}
                                className="px-2 py-1 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-xs font-medium rounded hover:from-purple-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                → Fakturering
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openArchiveDialog(case_.id);
                                }}
                                className="px-2 py-1 bg-gradient-to-r from-gray-500 to-gray-600 text-white text-xs font-medium rounded hover:from-gray-600 hover:to-gray-700 transition-all"
                              >
                                → Arkiv
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Vis kontaktinfo hvis første ordre har det */}
                    {(customerCases[0].tlf || customerCases[0].mail) && (
                      <div className="px-4 py-2 bg-orange-50 border-t text-xs">
                        <div className="flex space-x-4">
                          {customerCases[0].tlf && (
                            <div className="flex items-center space-x-1">
                              <Phone size={12} className="text-orange-600" />
                              <span className="font-medium">{customerCases[0].tlf}</span>
                            </div>
                          )}
                          {customerCases[0].mail && (
                            <div className="flex items-center space-x-1">
                              <Mail size={12} className="text-orange-600" />
                              <span>{customerCases[0].mail}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Arkiv Dialog */}
      {showArchiveDialog && selectedCaseId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Arkiver og marker som utlevert
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Hvem leverte ut maskinen til kunden?
              </p>
            </div>
            
            <div className="p-6">
              <div className="space-y-2">
                {utlevertPersons.map((person) => (
                  <button
                    key={person}
                    onClick={() => setSelectedPerson(person)}
                    className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                      selectedPerson === person
                        ? "border-green-500 bg-green-50 text-green-700"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {person}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowArchiveDialog(false);
                  setSelectedCaseId(null);
                  setSelectedPerson("");
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Avbryt
              </button>
              <button
                onClick={() => {
                  if (selectedPerson && selectedCaseId) {
                    handleMoveToArchive(selectedCaseId, selectedPerson);
                  }
                }}
                disabled={!selectedPerson}
                className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Arkiver
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default KlarTilHenting;